import React, { useState, useEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import api from "../../api";
import { useMealMate } from "../../context/MealMateContext";
import { useNavigate } from "react-router-dom";
import SideNavAccount from "../../components/SideNav/SideNavAccount";
import dayjs from "dayjs";
import { Row, Col } from "antd";

// ======================= Animations =======================
const slideInDown = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, -100%, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const slideOutUp = keyframes`
  from {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
  to {
    opacity: 0;
    transform: translate3d(0, -100%, 0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

// ======================= Styled Components =======================
const FavoriteListsPageWrapper = styled.div`
  display: flex;
  min-height: 100vh;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SideNavContainer = styled.div`
  width: 250px;
  background: #f8f9fa;

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    min-height: 60px;
  }
`;

const MainContent = styled.div`
  flex: 1;
  padding: 1rem;
  background-color: #f9f9f9;

  @media (min-width: 576px) {
    padding: 1.5rem;
  }

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const PageHeader = styled(Row)`
  margin-bottom: 1.5rem;

  @media (max-width: 576px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const HeaderTitle = styled(Col)`
  h1 {
    color: #333;
    font-size: 1.5rem;
    margin-bottom: 0.5rem;

    @media (min-width: 768px) {
      font-size: 2rem;
    }
  }
  p {
    color: #666;
    font-size: 0.9rem;

    @media (min-width: 576px) {
      font-size: 1rem;
    }
  }
`;

// Tab Navigation
const TabNavigation = styled(Row)`
  background: white;
  border-radius: 15px;
  padding: 0.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const TabButton = styled(Col)`
  flex: 1;
  padding: 1rem;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  background: ${(props) =>
    props.active ? "linear-gradient(135deg, #ff6b6b, #ff4b4b)" : "transparent"};
  color: ${(props) => (props.active ? "white" : "#666")};
  text-align: center;

  &:hover {
    background: ${(props) =>
      props.active ? "linear-gradient(135deg, #ff6b6b, #ff4b4b)" : "#f5f5f5"};
    transform: translateY(-1px);
  }

  @media (min-width: 576px) {
    font-size: 1rem;
  }
`;

const Section = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  @media (min-width: 576px) {
    padding: 2rem;
  }
`;

const ListContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  justify-content: center;
`;

const ListItemCard = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-bottom: 1rem;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 576px) {
    min-height: 300px;
  }
`;

const ItemImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
  border-bottom: 1px solid #eee;
  margin-bottom: 1rem;

  @media (max-width: 576px) {
    height: 140px;
  }
`;

const ItemDetails = styled.div`
  padding: 0 1rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  h3 {
    font-size: 1rem;
    color: #333;
    margin-bottom: 0.5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;

    @media (min-width: 576px) {
      font-size: 1.2rem;
    }
  }

  p {
    font-size: 0.8rem;
    color: #666;
    margin-bottom: 0.3rem;

    @media (min-width: 576px) {
      font-size: 0.9rem;
    }
  }
`;

// Menu Card specific styles
const MenuCard = styled(ListItemCard)`
  height: auto;
  min-height: 350px;

  @media (max-width: 576px) {
    min-height: 300px;
  }
`;

const MenuDate = styled.div`
  background: linear-gradient(135deg, #ff6b6b, #ff4b4b);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 1rem;

  @media (min-width: 576px) {
    font-size: 0.9rem;
  }
`;

const MenuNutrition = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  margin: 1rem 0;
  font-size: 0.7rem;
  color: #666;

  div {
    background: #f8f9fa;
    padding: 0.3rem 0.5rem;
    border-radius: 5px;
    text-align: center;
  }

  @media (min-width: 576px) {
    font-size: 0.8rem;
  }
`;

const MenuMeals = styled.div`
  text-align: left;
  width: 100%;
  margin: 1rem 0;

  h4 {
    color: #333;
    font-size: 0.8rem;
    margin-bottom: 0.5rem;
    border-bottom: 1px solid #eee;
    padding-bottom: 0.3rem;

    @media (min-width: 576px) {
      font-size: 0.9rem;
    }
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      font-size: 0.7rem;
      color: #666;
      margin-bottom: 0.2rem;
      padding-left: 0.5rem;
      position: relative;

      &:before {
        content: "•";
        color: #ff4b4b;
        position: absolute;
        left: 0;
      }

      @media (min-width: 576px) {
        font-size: 0.8rem;
      }
    }
  }
`;

const ViewMenuButton = styled.button`
  background-color: #1890ff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.7rem 0.9rem;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 500;
  transition: all 0.3s ease;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: #007bff;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(0, 123, 255, 0.3);
  }

  @media (max-width: 576px) {
    font-size: 0.8rem;
    padding: 0.6rem 0.8rem;
  }
`;

const ActionButton = styled.button`
  background-color: #ff4d4f;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.7rem 0.9rem;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 500;
  transition: all 0.3s ease;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: #dc3545;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(220, 53, 69, 0.3);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 576px) {
    font-size: 0.8rem;
    padding: 0.6rem 0.8rem;
  }
`;

const NoDataMessage = styled.p`
  text-align: center;
  font-size: 1rem;
  color: #777;
  padding: 1.5rem;
  border: 1px dashed #ddd;
  border-radius: 10px;
  margin-top: 1rem;

  @media (min-width: 576px) {
    font-size: 1.1rem;
    padding: 2rem;
    margin-top: 2rem;
  }
`;

// ======================= Custom Modal Components =======================
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${(props) => (props.isClosing ? fadeOut : fadeIn)} 0.3s ease;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 15px;
  padding: 1.5rem;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  animation: ${(props) => (props.isClosing ? "scaleOut" : scaleIn)} 0.3s ease;

  @keyframes scaleOut {
    from {
      opacity: 1;
      transform: scale(1);
    }
    to {
      opacity: 0;
      transform: scale(0.8);
    }
  }

  @media (min-width: 576px) {
    padding: 2rem;
  }
`;

const ModalIcon = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto 1rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  background-color: ${(props) =>
    props.type === "warning"
      ? "#fff2e8"
      : props.type === "success"
      ? "#f0f9ff"
      : "#fee2e2"};
  color: ${(props) =>
    props.type === "warning"
      ? "#fa8c16"
      : props.type === "success"
      ? "#1890ff"
      : "#f5222d"};
`;

const ModalTitle = styled.h3`
  color: #333;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;

  @media (min-width: 576px) {
    font-size: 1.3rem;
  }
`;

const ModalMessage = styled.p`
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.5;

  @media (min-width: 576px) {
    margin-bottom: 2rem;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const ModalButton = styled.button`
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 80px;

  ${(props) =>
    props.variant === "primary"
      ? `
    background-color: #1890ff;
    color: white;
    &:hover {
      background-color: #40a9ff;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
    }
  `
      : props.variant === "danger"
      ? `
    background-color: #ff4d4f;
    color: white;
    &:hover {
      background-color: #ff7875;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(255, 77, 79, 0.3);
    }
  `
      : `
    background-color: #f5f5f5;
    color: #666;
    &:hover {
      background-color: #e6e6e6;
      transform: translateY(-1px);
    }
  `}

  @media (min-width: 576px) {
    padding: 0.7rem 1.5rem;
    font-size: 0.9rem;
  }
`;

// ======================= Toast Notification =======================
const ToastContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1001;
`;

const ToastItem = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  margin-bottom: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 300px;
  animation: ${(props) => (props.isClosing ? slideOutUp : slideInDown)} 0.4s
    ease;
  border-left: 4px solid
    ${(props) =>
      props.type === "success"
        ? "#52c41a"
        : props.type === "error"
        ? "#ff4d4f"
        : "#fa8c16"};
`;

const ToastIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  background-color: ${(props) =>
    props.type === "success"
      ? "#52c41a"
      : props.type === "error"
      ? "#ff4d4f"
      : "#fa8c16"};
  color: white;
`;

const ToastMessage = styled.div`
  flex: 1;
  color: #333;
  font-size: 0.9rem;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  &::after {
    content: "";
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #1890ff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  text-align: center;
  color: #666;
  margin-top: 1rem;
  font-size: 0.9rem;

  @media (min-width: 576px) {
    font-size: 1rem;
  }
`;

const FavoriteListsPage = () => {
  const [activeTab, setActiveTab] = useState("recipes");
  const [favoriteRecipes, setFavoriteRecipes] = useState([]);
  const [favoriteMenus, setFavoriteMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalType, setModalType] = useState("recipe");
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [toasts, setToasts] = useState([]);
  const navigate = useNavigate();
  const { user, favoriteRecipesNeedRefresh } = useMealMate();

  const showToast = useCallback((message, type = "success") => {
    const id = Date.now();
    const newToast = { id, message, type, isClosing: false };
    setToasts((prev) => [...prev, newToast]);

    setTimeout(() => {
      setToasts((prev) =>
        prev.map((toast) =>
          toast.id === id ? { ...toast, isClosing: true } : toast
        )
      );

      setTimeout(() => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
      }, 400);
    }, 4000);
  }, []);

  const fetchFavoriteRecipes = useCallback(async () => {
    if (!user) return;
    try {
      const favoriteResponse = await api.get("/recipes/favorites");
      const favoriteIds = favoriteResponse.data.data;

      if (!favoriteIds || !Array.isArray(favoriteIds)) {
        setFavoriteRecipes([]);
        return;
      }

      const recipesResponse = await api.get("/recipes");
      const allRecipes = recipesResponse.data.data;

      const favoriteRecipes = allRecipes
        .filter((recipe) => favoriteIds.includes(recipe._id))
        .map((recipe) => ({
          ...recipe,
          isFavorite: true,
        }));

      setFavoriteRecipes(favoriteRecipes);
    } catch (error) {
      console.error("Lỗi khi lấy danh sách món ăn yêu thích:", error);
      showToast("Không thể tải danh sách món ăn yêu thích.", "error");
      setFavoriteRecipes([]);
    }
  }, [user, showToast]);

  const fetchFavoriteMenus = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get("/daily/menu/favorites");
      setFavoriteMenus(res.data || []);
    } catch (error) {
      console.error("Lỗi khi lấy danh sách menu yêu thích:", error);
      showToast("Không thể tải danh sách menu yêu thích.", "error");
    }
  }, [user, showToast]);

  const fetchData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      await Promise.all([fetchFavoriteRecipes(), fetchFavoriteMenus()]);
    } finally {
      setLoading(false);
    }
  }, [user, fetchFavoriteRecipes, fetchFavoriteMenus]);

  useEffect(() => {
    fetchData();
  }, [fetchData, favoriteRecipesNeedRefresh]);

  const handleShowConfirm = useCallback((item, type) => {
    setSelectedItem(item);
    setModalType(type);
    setShowConfirmModal(true);
    setIsModalClosing(false);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalClosing(true);
    setTimeout(() => {
      setShowConfirmModal(false);
      setSelectedItem(null);
      setModalType("recipe");
      setIsModalClosing(false);
    }, 300);
  }, []);

  const handleRemoveFavoriteRecipe = useCallback(async () => {
    if (!user || !selectedItem) {
      showToast("Bạn cần đăng nhập để thực hiện chức năng này.", "warning");
      return;
    }

    try {
      await api.delete(`/recipes/favorites/${selectedItem._id}`);
      showToast("Đã bỏ món ăn khỏi danh sách yêu thích.", "success");
      await fetchFavoriteRecipes();
      handleCloseModal();
    } catch (error) {
      console.error("Lỗi khi bỏ món ăn khỏi yêu thích:", error);
      showToast("Lỗi khi bỏ món ăn khỏi yêu thích.", "error");
    }
  }, [user, selectedItem, fetchFavoriteRecipes, handleCloseModal, showToast]);

  const handleRemoveFavoriteMenu = useCallback(async () => {
    if (!user || !selectedItem) {
      showToast("Bạn cần đăng nhập để thực hiện chức năng này.", "warning");
      return;
    }

    try {
      await api.put(`/daily/menu/${selectedItem._id}/favorite`);
      showToast("Đã bỏ menu khỏi danh sách yêu thích.", "success");
      await fetchFavoriteMenus();
      handleCloseModal();
    } catch (error) {
      console.error("Lỗi khi bỏ menu khỏi yêu thích:", error);
      showToast("Lỗi khi bỏ menu khỏi yêu thích.", "error");
    }
  }, [user, selectedItem, fetchFavoriteMenus, handleCloseModal, showToast]);

  const convertDateToMatchDB = (inputDate) => {
    const date = dayjs(inputDate);
    return date.utc().format("YYYY-MM-DDTHH:mm:ss.SSSZ");
  };
  const handleViewMenu = useCallback(
    (menu) => {
      const adjustedDate = convertDateToMatchDB(menu.date);
      navigate(`/account/daily-menu`, {
        state: { selectedDate: adjustedDate },
      });
    },
    [navigate]
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("vi-VN", {
      weekday: "long",
      day: "numeric",
      month: "numeric",
      year: "numeric",
    });
  };

  const renderMealsList = (meals) => {
    const mealTypes = {
      breakfast: "Sáng",
      lunch: "Trưa",
      dinner: "Tối",
      snack: "Ăn vặt",
    };

    return Object.entries(meals || {})
      .map(([mealType, mealList]) => {
        if (!mealList || mealList.length === 0) return null;

        return (
          <div key={mealType}>
            <h4>{mealTypes[mealType] || mealType}</h4>
            <ul>
              {mealList.slice(0, 3).map((meal, index) => (
                <li key={index}>
                  {meal.recipe?.name} ({meal.quantity || 1} phần)
                </li>
              ))}
              {mealList.length > 3 && (
                <li style={{ fontStyle: "italic" }}>
                  ... và {mealList.length - 3} món khác
                </li>
              )}
            </ul>
          </div>
        );
      })
      .filter(Boolean);
  };

  if (loading) {
    return (
      <FavoriteListsPageWrapper>
        <SideNavContainer>
          <SideNavAccount />
        </SideNavContainer>
        <MainContent>
          <PageHeader gutter={[0, 16]}>
            <HeaderTitle xs={24}>
              <h1>Danh sách yêu thích</h1>
              <p>Quản lý món ăn và menu yêu thích của bạn</p>
            </HeaderTitle>
          </PageHeader>
          <Section>
            <LoadingSpinner />
            <LoadingText>Đang tải danh sách yêu thích...</LoadingText>
          </Section>
        </MainContent>
      </FavoriteListsPageWrapper>
    );
  }

  return (
    <FavoriteListsPageWrapper>
      <SideNavContainer>
        <SideNavAccount />
      </SideNavContainer>
      <MainContent>
        <PageHeader gutter={[0, 16]}>
          <HeaderTitle xs={24}>
            <h1>Danh sách yêu thích</h1>
            <p>Quản lý món ăn và menu yêu thích của bạn</p>
          </HeaderTitle>
        </PageHeader>

        <TabNavigation gutter={8}>
          <TabButton
            xs={12}
            active={activeTab === "recipes"}
            onClick={() => setActiveTab("recipes")}
          >
            Món ăn yêu thích ({favoriteRecipes.length})
          </TabButton>
          <TabButton
            xs={12}
            active={activeTab === "menus"}
            onClick={() => setActiveTab("menus")}
          >
            Menu yêu thích ({favoriteMenus.length})
          </TabButton>
        </TabNavigation>

        <Section>
          {activeTab === "recipes" ? (
            favoriteRecipes.length > 0 ? (
              <ListContainer>
                {favoriteRecipes.map((recipe) => (
                  <ListItemCard key={recipe._id}>
                    <ItemImage
                      src={
                        recipe.image ||
                        recipe.imageUrl ||
                        "https://via.placeholder.com/280x180?text=Món+ăn"
                      }
                      alt={recipe.name || "Món ăn"}
                    />
                    <ItemDetails>
                      <h3>{recipe.name}</h3>
                      <p>{Math.round(recipe.calories)} kcal</p>
                      <p>
                        Protein: {Math.round(recipe.nutrition?.protein || 0)}g |
                        Béo: {Math.round(recipe.nutrition?.fat || 0)}g | Carb:{" "}
                        {Math.round(recipe.nutrition?.carbs || 0)}g
                      </p>
                    </ItemDetails>
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        marginTop: "auto",
                        width: "100%",
                        padding: "0 1rem",
                      }}
                    >
                      <ViewMenuButton
                        style={{ flex: 1 }}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/recipes/${recipe._id}`);
                        }}
                      >
                        👁 Xem chi tiết
                      </ViewMenuButton>
                      <ActionButton
                        style={{ flex: 1 }}
                        onClick={(e) => {
                          e.preventDefault();
                          handleShowConfirm(recipe, "recipe");
                        }}
                      >
                        ❌ Bỏ yêu thích
                      </ActionButton>
                    </div>
                  </ListItemCard>
                ))}
              </ListContainer>
            ) : (
              <NoDataMessage>Chưa có món ăn yêu thích nào.</NoDataMessage>
            )
          ) : favoriteMenus.length > 0 ? (
            <ListContainer>
              {favoriteMenus.map((favMenu) => (
                <MenuCard key={favMenu._id}>
                  <MenuDate>🗓 {formatDate(favMenu.date)}</MenuDate>

                  <ItemDetails>
                    <h3>
                      {favMenu.title ||
                        `Menu ngày ${new Date(favMenu.date).getDate()}/${
                          new Date(favMenu.date).getMonth() + 1
                        }`}
                    </h3>

                    <MenuNutrition>
                      <div>
                        <strong>
                          {Math.round(favMenu.totalCalories || 0)}
                        </strong>
                        <br />
                        <small>kcal</small>
                      </div>
                      <div>
                        <strong>
                          {Math.round(favMenu.totalProtein || 0)}g
                        </strong>
                        <br />
                        <small>Protein</small>
                      </div>
                      <div>
                        <strong>{Math.round(favMenu.totalCarbs || 0)}g</strong>
                        <br />
                        <small>Carbs</small>
                      </div>
                      <div>
                        <strong>{Math.round(favMenu.totalFat || 0)}g</strong>
                        <br />
                        <small>Fat</small>
                      </div>
                    </MenuNutrition>

                    <MenuMeals>{renderMealsList(favMenu.meals)}</MenuMeals>
                  </ItemDetails>

                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      marginTop: "auto",
                      width: "100%",
                    }}
                  >
                    <ViewMenuButton
                      style={{ flex: 1 }}
                      onClick={() => handleViewMenu(favMenu)}
                    >
                      👁 Xem chi tiết
                    </ViewMenuButton>
                    <ActionButton
                      style={{ flex: 1, marginTop: "0" }}
                      onClick={() => handleShowConfirm(favMenu, "menu")}
                    >
                      ❌ Bỏ yêu thích
                    </ActionButton>
                  </div>
                </MenuCard>
              ))}
            </ListContainer>
          ) : (
            <NoDataMessage>Chưa có menu yêu thích nào.</NoDataMessage>
          )}
        </Section>
      </MainContent>

      {showConfirmModal && (
        <ModalOverlay isClosing={isModalClosing} onClick={handleCloseModal}>
          <ModalContent
            isClosing={isModalClosing}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalIcon type="warning">⚠️</ModalIcon>
            <ModalTitle>Xác nhận bỏ yêu thích</ModalTitle>
            <ModalMessage>
              Bạn có chắc chắn muốn bỏ {modalType === "recipe" ? "món" : "menu"}{" "}
              "
              {modalType === "recipe"
                ? selectedItem?.name
                : `ngày ${
                    selectedItem
                      ? new Date(selectedItem.date).toLocaleDateString("vi-VN")
                      : ""
                  }`}
              " khỏi danh sách yêu thích không?
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={handleCloseModal}>Hủy</ModalButton>
              <ModalButton
                variant="danger"
                onClick={
                  modalType === "recipe"
                    ? handleRemoveFavoriteRecipe
                    : handleRemoveFavoriteMenu
                }
              >
                Xác nhận
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      <ToastContainer>
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            type={toast.type}
            isClosing={toast.isClosing}
          >
            <ToastIcon type={toast.type}>
              {toast.type === "success"
                ? "✓"
                : toast.type === "error"
                ? "✕"
                : "!"}
            </ToastIcon>
            <ToastMessage>{toast.message}</ToastMessage>
          </ToastItem>
        ))}
      </ToastContainer>
    </FavoriteListsPageWrapper>
  );
};

export default FavoriteListsPage;
